import { hashKey, QueryFunction } from '@tanstack/react-query';
import {
  DocumentReference,
  DocumentSnapshot,
  FirestoreError,
  getDocFromCache,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import { getErrorReporter } from 'shared/services/error-tracking';
import { ID } from 'shared/types/id';
import { isProductionEnvironment } from 'shared/utils/is-environment';

import { queryClient } from './query-client';
import {
  registerSubscription,
  unregisterSubscription,
} from './subscription-registry';

export const createSubscriptionDoc = <T extends { id: ID }>(
  doc: () => Promise<DocumentReference<T>> | DocumentReference<T>,
): QueryFunction<T | undefined, string[]> => {
  return (context) => {
    getErrorReporter().addBreadcrumb({
      category: 'tanstack-query',
      message: JSON.stringify(context.queryKey),
    });

    // per-invocation: a closure-level firstRun never settles TanStack retry promises
    let firstRun = true;
    let latestSnapshotData: T;

    return new Promise(async (resolve, reject) => {
      const docRef = await doc();
      const queryHash = hashKey(context.queryKey);

      let unsubscribe: Unsubscribe | undefined;
      let cleanedUp = false;
      const cleanup = () => {
        if (cleanedUp) {
          return;
        }
        cleanedUp = true;
        unsubscribe?.();
      };

      const onSuccess = (response: DocumentSnapshot<T>) => {
        latestSnapshotData = (response.data() as T) ?? null;

        if (firstRun) {
          resolve(latestSnapshotData);
          firstRun = false;
          return;
        }

        queryClient.setQueryData(context.queryKey, latestSnapshotData);
      };

      const onError = (error: FirestoreError) => {
        if (!isProductionEnvironment) {
          // eslint-disable-next-line no-console -- non-prod logging
          console.warn(
            `[Debug-Subscription] ERROR doc ${JSON.stringify(context.queryKey)} at ${performance.now().toFixed(0)}ms`,
            error,
          );
        }
        getErrorReporter().captureException(error, {
          tags: {
            source: 'firestore-subscription-doc',
            queryKey: JSON.stringify(context.queryKey),
          },
          contexts: { subscriptionDoc: { queryKey: context.queryKey } },
        });

        if (firstRun) {
          reject(error);
          firstRun = false;
          return;
        }

        // Safe to invalidate now: the re-executed queryFn supersedes this generation via the registry.
        unregisterSubscription(queryHash, cleanup);
        cleanup();
        queryClient.invalidateQueries({
          queryKey: context.queryKey,
          exact: true,
        });
      };

      getDocFromCache(docRef)
        .then(onSuccess)
        .catch(() => {
          // this is just cache, do nothing with this error
        });

      unsubscribe = onSnapshot(docRef, onSuccess, onError);
      registerSubscription(queryHash, cleanup);

      context.signal?.addEventListener('abort', () => {
        unregisterSubscription(queryHash, cleanup);
        cleanup();
        if (firstRun) {
          firstRun = false;
          reject(new Error('Query aborted by React Query'));
        }
      });
    });
  };
};
